import React from 'react';
import { useQuery } from 'react-query';
import { KatSpinner } from '@amzn/katal-react';
import { UserPermissions } from 'src/features/types/backendApi';
import { getUserFromMidwayCredentials, getWebIdentityToken, getCurrentUser, isAFTXDomain } from 'src/utils/Auth';
import { getEnv } from 'src/utils/Environment';
import { getUserPermissions } from 'src/utils/Queries';
import { useUser } from './UserContext';

function AuthenticationWrapper( { children }: { children: React.ReactNode } )
{
    const { user, setUser, isInitialized, setIsInitialized } = useUser();
    const [ userInfo, setUserInfo ] = React.useState<{ username: string; warehouseId?: string } | null>( null );

    // Only fetch user ID if we don't have a user and aren't initialized
    React.useEffect( () =>
    {
        let mounted = true;

        async function initializeUser()
        {
            if ( !user && !isInitialized )
            {
                try
                {
                    const userInfo = await getCurrentUser();
                    if ( mounted )
                    {
                        setUserInfo( userInfo );
                    }
                }
                catch ( error )
                {
                    console.error( 'Failed to get user ID:', error );
                    setIsInitialized( true ); // Mark as initialized even on error
                }
            }
        }

        initializeUser();

        return () =>
        {
            mounted = false;
        };
    }, [ user, isInitialized, setIsInitialized ] );
    // Only fetch permissions if we have userInfo and no user yet
    const { isLoading } = useQuery(
        'userPermissions',
        async () =>
        {
            const permissions = await getUserPermissions( userInfo!.username );
            //comment the line above and uncomment this to override permission to test features
            //const permissions = { devAdmin: true, gslaAdmin: true }

            return permissions;
        },
        {
            enabled: !!userInfo && !user && !isInitialized,
            onSuccess: ( permissions ) =>
            {

                if ( userInfo && permissions )
                {
                    let isAllowedForGSLAOverrides = false;
                    if ( getEnv() === (
                        'development' || 'gamma'
                    ) )
                    {
                        isAllowedForGSLAOverrides = permissions?.devAdmin ?? false;
                    }
                    else
                    {
                        isAllowedForGSLAOverrides = permissions?.gslaAdmin ?? false;
                    }
                    const authenticatedUser = {
                        login: userInfo.username,
                        isAuthorizedForSLAOverrides: isAllowedForGSLAOverrides,
                        isDevAdmin: permissions?.devAdmin ?? false,
                        ppgAdmin: permissions?.ppgAdmin ?? false,
                        isAFTX: isAFTXDomain(),
                        warehouseId: userInfo.warehouseId
                    };
                    setUser( authenticatedUser );
                }
                setIsInitialized( true );
            },
            onError: () =>
            {
                setIsInitialized( true );
            },
            staleTime: Infinity,
            cacheTime: Infinity,
        },
    );

    // Show loading state only during initial load
    if ( !isInitialized && isLoading )
    {
        return <KatSpinner size='large' />;
    }

    return <>{children}</>;
}

export {AuthenticationWrapper};
export default AuthenticationWrapper;
